import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiLoader, FiRefreshCw, FiHeart, FiRotateCcw, FiRotateCw, FiCopy, FiZap, FiPackage, FiTrash2, FiShuffle, FiCheck, FiAlertTriangle, FiImage, FiLayers } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { FONTS, NAME_FONTS, TEXT_FONTS, loadFonts, pairedTextFont } from "../logo/fonts";
import { CONTAINERS, CONTAINER_IDS, ICON_IDS, ICONS, INDUSTRIES, INDUSTRY_IDS, iconMarkup } from "../logo/icons";
import { buildLogo, DECORS, EFFECTS, LAYOUTS, NAME_TONES } from "../logo/engine";
import { MOOD_IDS, MOODS, brandFrom, generateDesigns } from "../logo/generate";
import { PALETTES, contrast, harmonies, normaliseHex } from "../logo/color";
import { MOCKUPS } from "../logo/mockups";
import { brandGuideSvg, brandKitZip, canvasBlob, faviconFiles, fileSlug, logoToBlob, saveBlob, socialFiles, svgBlob, svgToCanvas } from "../logo/exporters";
import JSZip from "jszip";

const STORE_FORM = "logo-studio-form-v1";
const STORE_SAVED = "logo-studio-saved-v1";
const EXAMPLES = [
  { name: "Aurora Coffee", tagline: "Fresh roasted daily", keywords: "coffee shop", colors: { primary: "#6f4e37", secondary: "#c8a27a", accent: "#2b1a10" } },
  { name: "Nexora Technologies", tagline: "Build the future", keywords: "software company", colors: { primary: "#2563eb", secondary: "#22d3ee", accent: "#0f172a" } },
  { name: "Green Leaf Organics", tagline: "Pure. Natural. Local.", keywords: "organic farm", colors: { primary: "#1f6f4a", secondary: "#7bc47f", accent: "#0d3b2e" } },
  { name: "Royal Estate", tagline: "Homes worth living in", keywords: "real estate", colors: { primary: "#14213d", secondary: "#fca311", accent: "#e5e5e5" } },
];

const inputClass = "w-full px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light";
const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-2.5";
const pillOn = "bg-accent-light text-navy border-accent-light";
const pillOff = "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light";
const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const card = "rounded-2xl bg-white/5 border border-navy-border p-5 md:p-6";
const CHECKER = "bg-[repeating-conic-gradient(#d9dde4_0%_25%,#f4f6f9_0%_50%)] bg-[length:20px_20px]";

const readJson = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };
const writeJson = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ } };

function Pills({ value, options, onChange, label }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map(([key, text]) => (
        <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={value === key}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${value === key ? pillOn : pillOff}`}>{text}</button>
      ))}
    </div>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, suffix = "" }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{value}{suffix}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label={label} />
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <div>
      <span className={labelClass}>{label}</span>
      <div className="flex items-center gap-2">
        <input type="color" value={normaliseHex(value)} onChange={(e) => onChange(e.target.value)} aria-label={`${label} colour`} className="w-11 h-11 rounded-lg border border-navy-border bg-transparent p-0.5 cursor-pointer shrink-0" />
        <input value={text} onChange={(e) => { setText(e.target.value); if (/^#?[0-9a-f]{6}$/i.test(e.target.value.trim())) onChange(normaliseHex(e.target.value)); }}
          onBlur={() => setText(value)} aria-label={`${label} hex code`} maxLength={7} className={`${inputClass} font-mono !py-2.5`} spellCheck={false} />
      </div>
    </div>
  );
}

// An SVG logo (or mockup) filling its box.
const SvgBox = React.memo(function SvgBox({ svg, className = "" }) {
  return <div className={`[&>svg]:w-full [&>svg]:h-full [&>svg]:block ${className}`} dangerouslySetInnerHTML={{ __html: svg }} />;
});

function IconThumb({ id, color = "#fff", accent = "#ffd166", size = 28 }) {
  return <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" dangerouslySetInnerHTML={{ __html: iconMarkup(id, { main: color, accent }, `th${id}`) }} />;
}

function ShapeThumb({ id }) {
  const c = CONTAINERS[id];
  return (
    <svg viewBox="0 0 100 100" width="26" height="26" aria-hidden="true">
      {c.d ? <path d={c.d} fill="currentColor" /> : <path d="M20 20L80 80M80 20L20 80" stroke="currentColor" strokeWidth="9" strokeLinecap="round" />}
    </svg>
  );
}

export default function LogoGeneratorTool() {
  const [fontsReady, setFontsReady] = useState(false);
  const [fontError, setFontError] = useState("");
  const [form, setForm] = useState(() => ({ name: "", tagline: "", keywords: "", industry: "auto", mood: "auto", colors: { primary: "#0b5ed7", secondary: "#00b4d8", accent: "#0a2540" }, ...readJson(STORE_FORM, {}) }));
  const [seed, setSeed] = useState(1);
  const [designs, setDesigns] = useState([]);
  const [config, setConfig] = useState(null);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [bgMode, setBgMode] = useState("light");
  const [tab, setTab] = useState("layout");
  const [view, setView] = useState("designs"); // designs | mockups | download
  const [saved, setSaved] = useState(() => readJson(STORE_SAVED, []));
  const [taglines, setTaglines] = useState({ list: [], loading: false, note: "" });
  const [fieldError, setFieldError] = useState("");
  const [fabric, setFabric] = useState("white");
  const [pngWidth, setPngWidth] = useState(2048);
  const [pngBg, setPngBg] = useState("transparent");
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState("");
  const lastEdit = useRef({ key: "", at: 0 });
  const resultRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadFonts(Object.keys(FONTS)).then(() => alive && setFontsReady(true)).catch(() => alive && setFontError("The fonts could not be loaded. Check your connection and reload the page."));
    return () => { alive = false; };
  }, []);

  useEffect(() => { writeJson(STORE_FORM, form); }, [form]);
  useEffect(() => { writeJson(STORE_SAVED, saved); }, [saved]);

  // ---------------------------------------------------------------------------------------- designing
  const setColors = (patch) => setForm((f) => ({ ...f, colors: { ...f.colors, ...patch } }));
  const brand = useMemo(() => brandFrom(form), [form]);
  const industry = INDUSTRIES[brand.industry];

  const generate = useCallback((nextSeed) => {
    setFieldError("");
    if (form.name.trim().length < 2) { setFieldError("Enter your brand name first (at least 2 characters)."); return; }
    if (form.name.trim().length > 40) { setFieldError("Please keep the brand name under 40 characters."); return; }
    const base = brandFrom(form);
    const list = generateDesigns(base, 12, nextSeed);
    setDesigns(list);
    setSeed(nextSeed);
    setConfig(list[0]);
    setPast([]);
    setFuture([]);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  }, [form]);

  const choose = (design) => {
    setPast((p) => (config ? [...p.slice(-49), config] : p));
    setFuture([]);
    setConfig(design);
    lastEdit.current = { key: "", at: 0 };
  };

  // Changes to the open design; quick successive changes to the same setting (a slider) count as one step for undo.
  const edit = (patch, key = "") => {
    setConfig((current) => {
      const next = { ...current, ...patch, colors: { ...current.colors, ...(patch.colors || {}) } };
      const now = Date.now();
      if (!(key && lastEdit.current.key === key && now - lastEdit.current.at < 900)) setPast((p) => [...p.slice(-49), current]);
      lastEdit.current = { key, at: now };
      setFuture([]);
      return next;
    });
  };
  const undo = () => { if (!past.length) return; setFuture((f) => [config, ...f]); setConfig(past[past.length - 1]); setPast((p) => p.slice(0, -1)); lastEdit.current = { key: "", at: 0 }; };
  const redo = () => { if (!future.length) return; setPast((p) => [...p, config]); setConfig(future[0]); setFuture((f) => f.slice(1)); lastEdit.current = { key: "", at: 0 }; };

  const applyPalette = (primary, secondary, accent) => { setColors({ primary, secondary, accent }); if (config) edit({ colors: { primary, secondary, accent } }); };

  const logo = useMemo(() => (fontsReady && config ? buildLogo(config, { uid: "main" }) : null), [fontsReady, config]);
  const onDark = bgMode === "dark";
  const onBrand = bgMode === "brand";
  const shownLogo = useMemo(() => (fontsReady && config ? buildLogo(config, { uid: "show", mode: onDark ? "light" : onBrand ? "white" : "color" }) : null), [fontsReady, config, onDark, onBrand]);
  const variants = useMemo(() => {
    if (!fontsReady || !config) return [];
    const base = { ...config };
    const symbolOnly = { ...config, layout: "iconOnly", source: config.source === "none" ? "letters" : config.source };
    return [
      ["Full colour", buildLogo(base, { uid: "v1" }), "#ffffff"],
      ["On dark", buildLogo(base, { uid: "v2", mode: "light" }), "#0f172a"],
      ["Black", buildLogo(base, { uid: "v3", mode: "black" }), "#ffffff"],
      ["White on colour", buildLogo(base, { uid: "v4", mode: "white" }), config.colors.primary],
      ["Symbol only", buildLogo(symbolOnly, { uid: "v5" }), "#f3f4f6"],
    ];
  }, [fontsReady, config]);

  const thumbs = useMemo(() => (fontsReady ? designs.map((d, i) => buildLogo(d, { uid: `g${i}`, pad: 0.08 })) : []), [fontsReady, designs]);
  const mockups = useMemo(() => (fontsReady && config && view === "mockups" ? MOCKUPS.map((m) => ({ ...m, ...m.build(config, fabric) })) : []), [fontsReady, config, view, fabric]);

  const warnings = useMemo(() => {
    const list = [];
    if (!config) return list;
    if (logo?.missing?.length) list.push(`The font has no shape for: ${logo.missing.join(" ")}  - choose another font, or use Latin letters.`);
    if (contrast(config.colors.primary, "#ffffff") < 1.5) list.push("Your primary colour is very light: the logo will be hard to see on white.");
    if (contrast(config.colors.primary, "#0f172a") < 1.5) list.push("Your primary colour is very dark: use the \"On dark\" version on dark backgrounds.");
    return list;
  }, [config, logo]);

  // ---------------------------------------------------------------------------------------- saved logos
  const isSaved = config && saved.some((s) => JSON.stringify(s.config) === JSON.stringify(config));
  const toggleSave = () => {
    if (!config) return;
    if (isSaved) setSaved((list) => list.filter((s) => JSON.stringify(s.config) !== JSON.stringify(config)));
    else setSaved((list) => [{ id: Date.now(), config }, ...list].slice(0, 24));
  };

  // ---------------------------------------------------------------------------------------- taglines
  const suggestTaglines = async () => {
    setTaglines({ list: [], loading: true, note: "" });
    const { ok, data } = await apiRequest("/core/logo/taglines/", { method: "POST", body: { name: form.name, keywords: form.keywords, industry: brand.industry, mood: brand.mood } });
    if (!ok) { setTaglines({ list: [], loading: false, note: errorMessage(data) }); return; }
    setTaglines({ list: data.taglines || [], loading: false, note: data.engine === "built-in" ? "Ideas from the built-in writer." : "" });
  };
  const applyTagline = (text) => {
    setForm((f) => ({ ...f, tagline: text }));
    if (config) edit({ tagline: text });
  };

  // ---------------------------------------------------------------------------------------- downloads
  const run = async (name, job) => {
    setBusy(name);
    setProgress(0);
    setNotice("");
    try { await job(); } catch (e) { setNotice(e.message || "Something went wrong while saving. Please try again."); } finally { setBusy(""); }
  };
  const slug = fileSlug(config?.name);
  const bgColor = pngBg === "white" ? "#ffffff" : pngBg === "dark" ? "#0f172a" : pngBg === "brand" ? config?.colors.primary : null;
  const exportMode = pngBg === "dark" ? "light" : pngBg === "brand" ? "white" : "color";
  const downloadSvg = () => run("svg", async () => saveBlob(`${slug}-logo.svg`, svgBlob(buildLogo(config, { uid: "ex" }).svg)));
  const downloadPng = () => run("png", async () => saveBlob(`${slug}-logo-${pngWidth}.png`, await logoToBlob(buildLogo(config, { uid: "ex", mode: exportMode }), pngWidth, { background: bgColor })));
  const downloadJpg = () => run("jpg", async () => saveBlob(`${slug}-logo-${pngWidth}.jpg`, await logoToBlob(buildLogo(config, { uid: "ex", mode: exportMode }), pngWidth, { type: "image/jpeg", background: bgColor || "#ffffff" })));
  const copySvg = () => run("copy", async () => { await navigator.clipboard.writeText(buildLogo(config, { uid: "ex" }).svg); setNotice("SVG code copied."); });
  const zipFiles = (name, files) => run(name, async () => {
    const zip = new JSZip();
    Object.entries(files).forEach(([n, b]) => zip.file(n, b));
    saveBlob(`${slug}-${name}.zip`, await zip.generateAsync({ type: "blob" }));
  });
  const downloadFavicons = async () => run("favicon", async () => zipFiles("favicons", await faviconFiles(config)));
  const downloadSocial = async () => run("social", async () => zipFiles("social-media", await socialFiles(config)));
  const downloadGuide = () => run("guide", async () => saveBlob(`${slug}-brand-guide.png`, await canvasBlob(await svgToCanvas(brandGuideSvg(config), 1600, 1100))));
  const downloadKit = () => run("kit", async () => {
    const built = MOCKUPS.map((m) => ({ id: m.id, ...m.build(config, "white") }));
    saveBlob(`${slug}-brand-kit.zip`, await brandKitZip(config, built, setProgress));
  });
  const downloadMockup = (m) => run(`mock-${m.id}`, async () => saveBlob(`${slug}-${m.id}-mockup.png`, await canvasBlob(await svgToCanvas(m.svg, 2400, 1600))));

  const previewBg = onDark ? "bg-[#0f172a]" : onBrand ? "" : bgMode === "transparent" ? CHECKER : "bg-white";

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Logo &amp; Design Generator</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Enter your brand name and your colours and get a dozen professional logo designs at once - then fine-tune any of them. Download the logo as SVG or PNG,
        or take the complete brand kit: favicons, social media pictures, a brand guide and mockups on cards, shirts, signs and phones.
      </p>

      {/* ------------------------------------------------------------------------------ the brief */}
      <div className={`${card} space-y-6`}>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="logo-name" className={labelClass}>Brand name</label>
            <input id="logo-name" className={inputClass} value={form.name} maxLength={40} placeholder="e.g. Aurora Coffee" onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && fontsReady && generate(seed + 1)} dir="auto" autoComplete="off" />
          </div>
          <div>
            <label htmlFor="logo-tagline" className={labelClass}>Tagline <span className="normal-case tracking-normal text-gray-500 font-normal">(optional)</span></label>
            <div className="flex gap-2">
              <input id="logo-tagline" className={inputClass} value={form.tagline} maxLength={50} placeholder="e.g. Fresh roasted daily" onChange={(e) => setForm({ ...form, tagline: e.target.value })} dir="auto" autoComplete="off" />
              <button type="button" onClick={suggestTaglines} disabled={taglines.loading || form.name.trim().length < 2} className={`${ghost} shrink-0`} title="Suggest taglines">
                {taglines.loading ? <FiLoader className="animate-spin" size={14} /> : <FiZap size={14} />} Ideas
              </button>
            </div>
          </div>
        </div>

        {(taglines.list.length > 0 || taglines.note) && (
          <div className="flex flex-wrap gap-2 items-center -mt-2">
            {taglines.list.map((t) => <button key={t} type="button" onClick={() => applyTagline(t)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-200 hover:border-accent-light">{t}</button>)}
            {taglines.note && <span className="text-xs text-gray-500">{taglines.note}</span>}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="logo-keywords" className={labelClass}>What does the business do? <span className="normal-case tracking-normal text-gray-500 font-normal">(optional)</span></label>
            <input id="logo-keywords" className={inputClass} value={form.keywords} maxLength={80} placeholder="e.g. coffee shop, law firm, mobile app" onChange={(e) => setForm({ ...form, keywords: e.target.value })} dir="auto" />
          </div>
          <div>
            <label htmlFor="logo-industry" className={labelClass}>Industry <span className="normal-case tracking-normal text-gray-500 font-normal">(chooses the symbols)</span></label>
            <select id="logo-industry" className={inputClass} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
              <option value="auto">Detect from my words{form.industry === "auto" && (form.name || form.keywords) ? ` - ${industry.label}` : ""}</option>
              {INDUSTRY_IDS.map((id) => <option key={id} value={id}>{INDUSTRIES[id].label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <span className={labelClass}>Style</span>
          <Pills label="Style" value={form.mood} onChange={(mood) => setForm({ ...form, mood })} options={[["auto", "Best for my industry"], ...MOOD_IDS.map((id) => [id, MOODS[id].label])]} />
        </div>

        <div>
          <span className={labelClass}>Your colours</span>
          <div className="grid sm:grid-cols-3 gap-4">
            <ColorField label="Primary" value={form.colors.primary} onChange={(v) => setColors({ primary: v })} />
            <ColorField label="Secondary" value={form.colors.secondary} onChange={(v) => setColors({ secondary: v })} />
            <ColorField label="Accent" value={form.colors.accent} onChange={(v) => setColors({ accent: v })} />
          </div>
          <div className="flex flex-wrap gap-2 mt-4" role="group" aria-label="Ready-made palettes">
            {PALETTES.map(([name, p, s, a]) => (
              <button key={name} type="button" onClick={() => setColors({ primary: p, secondary: s, accent: a })} title={name} aria-label={`${name} palette`}
                className="flex rounded-full overflow-hidden border border-white/20 hover:border-accent-light h-7 w-[66px]">
                <span className="flex-1" style={{ background: p }} /><span className="flex-1" style={{ background: s }} /><span className="flex-1" style={{ background: a }} />
              </button>
            ))}
          </div>
          <details className="mt-3 text-xs text-gray-400">
            <summary className="cursor-pointer hover:text-white">Colours that go with my primary colour</summary>
            <div className="flex flex-wrap gap-3 mt-3">
              {Object.entries(harmonies(form.colors.primary)).map(([name, list]) => (
                <button key={name} type="button" onClick={() => setColors({ secondary: list[1] || list[0], accent: list[2] || form.colors.accent })} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-navy-border hover:border-accent-light">
                  <span className="flex">{list.map((c) => <span key={c} className="w-4 h-4 rounded-full border border-white/20 -ml-1 first:ml-0" style={{ background: c }} />)}</span>
                  <span className="capitalize">{name}</span>
                </button>
              ))}
            </div>
          </details>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>Try an example:</span>
          {EXAMPLES.map((ex) => <button key={ex.name} type="button" onClick={() => setForm({ ...form, ...ex, industry: "auto", mood: "auto" })} className="px-3 py-1 rounded-full border border-navy-border text-gray-300 hover:border-accent-light">{ex.name}</button>)}
        </div>

        {fieldError && <p className="text-sm text-amber-300">{fieldError}</p>}
        {fontError && <p className="text-sm text-red-300">{fontError}</p>}
        <button type="button" onClick={() => generate(designs.length ? seed + 1 : seed)} disabled={!fontsReady}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-60">
          {fontsReady ? <><FiZap /> {designs.length ? "Generate new logos" : "Generate logos"}</> : <><FiLoader className="animate-spin" /> Preparing fonts...</>}
        </button>
      </div>

      <div ref={resultRef} />

      {/* ------------------------------------------------------------------------------ the editor */}
      {config && logo && (
        <div className="mt-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 items-start">
          <div className={`${card} lg:sticky lg:top-24`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your logo</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={undo} disabled={!past.length} className={ghost} aria-label="Undo"><FiRotateCcw size={13} /> Undo</button>
                <button type="button" onClick={redo} disabled={!future.length} className={ghost} aria-label="Redo"><FiRotateCw size={13} /> Redo</button>
                <button type="button" onClick={toggleSave} className={`${ghost} ${isSaved ? "!border-pink-400 text-pink-300" : ""}`} aria-pressed={!!isSaved}><FiHeart size={13} fill={isSaved ? "currentColor" : "none"} /> {isSaved ? "Saved" : "Save"}</button>
              </div>
            </div>
            <div className={`rounded-xl overflow-hidden border border-navy-border ${previewBg}`} style={onBrand ? { background: config.colors.primary } : undefined}>
              <SvgBox svg={shownLogo.svg} className="h-[300px] md:h-[380px]" />
            </div>
            <div className="flex flex-wrap gap-2 mt-3" role="group" aria-label="Preview background">
              {[["light", "On white"], ["dark", "On dark"], ["brand", "On brand colour"], ["transparent", "Transparent"]].map(([key, text]) => (
                <button key={key} type="button" onClick={() => setBgMode(key)} aria-pressed={bgMode === key} className={`px-3 py-1 rounded-full text-xs border ${bgMode === key ? pillOn : pillOff}`}>{text}</button>
              ))}
            </div>
            {warnings.map((w) => <p key={w} className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mt-3 flex gap-2"><FiAlertTriangle className="shrink-0 mt-0.5" /> {w}</p>)}
            <div className="grid grid-cols-5 gap-2 mt-4">
              {variants.map(([name, v, bg]) => (
                <figure key={name} className="text-center">
                  <div className="rounded-lg border border-navy-border overflow-hidden" style={{ background: bg }}><SvgBox svg={v.svg} className="h-14 md:h-16" /></div>
                  <figcaption className="text-[10px] text-gray-500 mt-1 leading-tight">{name}</figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className={card}>
            <div role="tablist" className="flex gap-1 rounded-full bg-white/5 border border-navy-border p-1 mb-5">
              {[["layout", "Layout & symbol"], ["text", "Name & text"], ["colors", "Colours & effects"]].map(([key, text]) => (
                <button key={key} role="tab" aria-selected={tab === key} type="button" onClick={() => setTab(key)}
                  className={`flex-1 px-2 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors ${tab === key ? "bg-brand-gradient text-white" : "text-gray-300 hover:text-white"}`}>{text}</button>
              ))}
            </div>

            {tab === "layout" && (
              <div className="space-y-5">
                <div><span className={labelClass}>Layout</span><Pills label="Layout" value={config.layout} onChange={(layout) => edit({ layout })} options={Object.entries(LAYOUTS)} /></div>
                <div><span className={labelClass}>Symbol</span><Pills label="Symbol" value={config.source} onChange={(source) => edit({ source })} options={[["icon", "Icon"], ["letters", "Initials"], ["none", "None"]]} /></div>
                {config.source === "letters" && (
                  <div>
                    <label htmlFor="logo-letters" className={labelClass}>Initials <span className="normal-case tracking-normal text-gray-500 font-normal">(empty = from the name)</span></label>
                    <input id="logo-letters" className={`${inputClass} !w-40 uppercase`} maxLength={3} value={config.letters || ""} onChange={(e) => edit({ letters: e.target.value }, "letters")} />
                  </div>
                )}
                {config.source === "icon" && (
                  <div>
                    <span className={labelClass}>Icon <span className="normal-case tracking-normal text-gray-500 font-normal">- suggestions for {industry.label.toLowerCase()} first</span></span>
                    <div className="grid grid-cols-7 sm:grid-cols-9 gap-1.5 max-h-56 overflow-y-auto pr-1" role="group" aria-label="Icon">
                      {[...new Set([...industry.icons, ...ICON_IDS])].map((id) => (
                        <button key={id} type="button" onClick={() => edit({ icon: id })} aria-pressed={config.icon === id} title={ICONS[id].label} aria-label={ICONS[id].label}
                          className={`aspect-square rounded-lg border flex items-center justify-center ${config.icon === id ? "border-accent-light bg-accent/20" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                          <IconThumb id={id} color={config.icon === id ? "#22d3ee" : "#e5e7eb"} accent={config.icon === id ? "#fbbf24" : "#9ca3af"} size={26} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {config.source !== "none" && config.layout !== "wordmark" && (
                  <>
                    <div>
                      <span className={labelClass}>Shape behind the symbol</span>
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Shape">
                        {CONTAINER_IDS.map((id) => (
                          <button key={id} type="button" onClick={() => edit({ container: id })} aria-pressed={config.container === id} title={CONTAINERS[id].label} aria-label={CONTAINERS[id].label}
                            className={`w-11 h-11 rounded-lg border flex items-center justify-center ${config.container === id ? "border-accent-light text-accent-light bg-accent/10" : "border-navy-border text-gray-400 hover:border-accent-light/60"}`}><ShapeThumb id={id} /></button>
                        ))}
                      </div>
                    </div>
                    {config.container !== "none" && <div><span className={labelClass}>Shape style</span><Pills label="Shape style" value={config.containerStyle} onChange={(containerStyle) => edit({ containerStyle })} options={[["solid", "Solid"], ["gradient", "Gradient"], ["outline", "Outline"]]} /></div>}
                    <Slider label="Symbol size" value={Math.round(config.iconScale * 100)} min={60} max={170} step={5} suffix="%" onChange={(v) => edit({ iconScale: v / 100 }, "iconScale")} />
                  </>
                )}
              </div>
            )}

            {tab === "text" && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label htmlFor="ed-name" className={labelClass}>Name</label><input id="ed-name" className={inputClass} value={config.name} maxLength={40} onChange={(e) => edit({ name: e.target.value }, "name")} dir="auto" /></div>
                  <div><label htmlFor="ed-tag" className={labelClass}>Tagline</label><input id="ed-tag" className={inputClass} value={config.tagline} maxLength={50} onChange={(e) => edit({ tagline: e.target.value }, "tagline")} dir="auto" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ed-font" className={labelClass}>Name font</label>
                    <select id="ed-font" className={inputClass} value={config.font} onChange={(e) => edit({ font: e.target.value, textFont: pairedTextFont(e.target.value) })}>
                      {NAME_FONTS.map((k) => <option key={k} value={k}>{FONTS[k].label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ed-tfont" className={labelClass}>Tagline font</label>
                    <select id="ed-tfont" className={inputClass} value={config.textFont} onChange={(e) => edit({ textFont: e.target.value })}>
                      {TEXT_FONTS.map((k) => <option key={k} value={k}>{FONTS[k].label}</option>)}
                    </select>
                  </div>
                </div>
                <div><span className={labelClass}>Name letters</span><Pills label="Name letters" value={config.nameCase} onChange={(nameCase) => edit({ nameCase })} options={[["upper", "UPPERCASE"], ["title", "Title Case"], ["lower", "lowercase"], ["asis", "As typed"]]} /></div>
                <div><span className={labelClass}>Name lines</span><Pills label="Name lines" value={config.nameLines} onChange={(nameLines) => edit({ nameLines })} options={[["auto", "Automatic"], ["1", "One line"], ["2", "Two lines"]]} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Slider label="Name spacing" value={config.tracking} min={0} max={300} step={5} onChange={(v) => edit({ tracking: v }, "tracking")} />
                  <Slider label="Name size" value={Math.round(config.nameScale * 100)} min={60} max={150} step={5} suffix="%" onChange={(v) => edit({ nameScale: v / 100 }, "nameScale")} />
                  <Slider label="Tagline spacing" value={config.taglineTracking} min={0} max={600} step={10} onChange={(v) => edit({ taglineTracking: v }, "tagTrack")} />
                  <Slider label="Tagline size" value={Math.round(config.taglineScale * 100)} min={60} max={170} step={5} suffix="%" onChange={(v) => edit({ taglineScale: v / 100 }, "tagScale")} />
                </div>
                <div><span className={labelClass}>Tagline letters</span><Pills label="Tagline letters" value={config.taglineCase} onChange={(taglineCase) => edit({ taglineCase })} options={[["upper", "UPPERCASE"], ["title", "Title Case"], ["lower", "lowercase"], ["asis", "As typed"]]} /></div>
                <div><span className={labelClass}>Name colour</span><Pills label="Name colour" value={config.nameTone} onChange={(nameTone) => edit({ nameTone })} options={Object.entries(NAME_TONES)} /></div>
                <div><span className={labelClass}>Decoration</span><Pills label="Decoration" value={config.decor} onChange={(decor) => edit({ decor })} options={Object.entries(DECORS)} /></div>
                <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer"><input type="checkbox" checked={config.justify} onChange={(e) => edit({ justify: e.target.checked })} className="accent-[#22d3ee] w-4 h-4" /> Stretch the tagline to the width of the name</label>
              </div>
            )}

            {tab === "colors" && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-3 gap-4">
                  <ColorField label="Primary" value={config.colors.primary} onChange={(v) => edit({ colors: { primary: v } }, "cp")} />
                  <ColorField label="Secondary" value={config.colors.secondary} onChange={(v) => edit({ colors: { secondary: v } }, "cs")} />
                  <ColorField label="Accent" value={config.colors.accent} onChange={(v) => edit({ colors: { accent: v } }, "ca")} />
                </div>
                <div>
                  <span className={labelClass}>Palettes</span>
                  <div className="flex flex-wrap gap-2">
                    {PALETTES.map(([name, p, s, a]) => (
                      <button key={name} type="button" onClick={() => applyPalette(p, s, a)} title={name} aria-label={`${name} palette`} className="flex rounded-full overflow-hidden border border-white/20 hover:border-accent-light h-7 w-[66px]">
                        <span className="flex-1" style={{ background: p }} /><span className="flex-1" style={{ background: s }} /><span className="flex-1" style={{ background: a }} />
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => edit({ colors: { primary: config.colors.secondary, secondary: config.colors.primary } })} className={`${ghost} mt-3`}><FiRefreshCw size={13} /> Swap primary and secondary</button>
                </div>
                <div><span className={labelClass}>Effect</span><Pills label="Effect" value={config.effect} onChange={(effect) => edit({ effect })} options={Object.entries(EFFECTS)} /></div>
                <p className="text-xs text-gray-500">3D depth, glossy and inner-line effects need a shape behind the symbol.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------------------ ideas, mockups, download */}
      {config && logo && (
        <div className="mt-10">
          <div role="tablist" className="inline-flex flex-wrap gap-1 rounded-full bg-white/5 border border-navy-border p-1 mb-6">
            {[["designs", "More ideas", FiLayers], ["mockups", "Mockups", FiImage], ["download", "Download", FiDownload]].map(([key, text, Icon]) => (
              <button key={key} role="tab" aria-selected={view === key} type="button" onClick={() => setView(key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${view === key ? "bg-brand-gradient text-white" : "text-gray-300 hover:text-white"}`}><Icon size={15} /> {text}</button>
            ))}
          </div>

          {view === "designs" && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <button type="button" onClick={() => generate(seed + 1)} className={ghost}><FiShuffle size={13} /> Shuffle all</button>
                <button type="button" onClick={() => { setDesigns(generateDesigns(config, 12, seed + 101, { layout: true, icon: true, font: true })); setSeed(seed + 101); }} className={ghost}><FiRefreshCw size={13} /> More like my current logo</button>
                <span className="text-xs text-gray-500 ms-auto">Click a design to edit it. Your colours are always used.</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {designs.map((d, i) => (
                  <div key={`${seed}-${i}`} className={`group rounded-xl overflow-hidden border bg-white relative ${JSON.stringify(d) === JSON.stringify(config) ? "border-accent-light ring-2 ring-accent-light/60" : "border-navy-border hover:border-accent-light"}`}>
                    <button type="button" onClick={() => choose(d)} className="block w-full" aria-label={`Use design ${i + 1}`}>
                      {thumbs[i] && <SvgBox svg={thumbs[i].svg} className="h-36 md:h-40 p-1" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === "mockups" && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-xs text-gray-400">Shirt and mug colour:</span>
                <Pills label="Fabric" value={fabric} onChange={setFabric} options={[["white", "White"], ["black", "Black"], ["brand", "Brand colour"]]} />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {mockups.map((m) => (
                  <figure key={m.id} className="rounded-xl overflow-hidden border border-navy-border bg-white/5">
                    <SvgBox svg={m.svg} className="aspect-[3/2]" />
                    <figcaption className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-semibold">{m.label}</span>
                      <button type="button" onClick={() => downloadMockup(m)} disabled={!!busy} className={ghost}>{busy === `mock-${m.id}` ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} PNG</button>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4">Mockups are illustrations to show how the logo can look; they are not photographs.</p>
            </div>
          )}

          {view === "download" && (
            <div className="grid md:grid-cols-2 gap-5">
              <div className={`${card} space-y-4`}>
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">The logo</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={downloadSvg} disabled={!!busy} className={ghost}><FiDownload size={13} /> SVG (vector)</button>
                  <button type="button" onClick={downloadPng} disabled={!!busy} className={ghost}>{busy === "png" ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} PNG</button>
                  <button type="button" onClick={downloadJpg} disabled={!!busy} className={ghost}><FiDownload size={13} /> JPG</button>
                  <button type="button" onClick={copySvg} disabled={!!busy} className={ghost}><FiCopy size={13} /> Copy SVG code</button>
                </div>
                <div>
                  <span className={labelClass}>PNG / JPG width</span>
                  <Pills label="Width" value={String(pngWidth)} onChange={(v) => setPngWidth(Number(v))} options={[["512", "512 px"], ["1024", "1024 px"], ["2048", "2048 px"], ["4096", "4096 px"]]} />
                </div>
                <div>
                  <span className={labelClass}>Background</span>
                  <Pills label="Background" value={pngBg} onChange={setPngBg} options={[["transparent", "Transparent (PNG)"], ["white", "White"], ["dark", "Dark"], ["brand", "Brand colour"]]} />
                </div>
                <p className="text-xs text-gray-500">The SVG has its text turned into outlines, so it opens exactly the same in Illustrator, Figma, Canva or a printer's software - no font needed.</p>
              </div>
              <div className={`${card} space-y-4`}>
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">Brand extras</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={downloadFavicons} disabled={!!busy} className={ghost}>{busy === "favicon" ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} Favicon pack</button>
                  <button type="button" onClick={downloadSocial} disabled={!!busy} className={ghost}>{busy === "social" ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} Social media pictures</button>
                  <button type="button" onClick={downloadGuide} disabled={!!busy} className={ghost}>{busy === "guide" ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} Brand guide (PNG)</button>
                </div>
                <button type="button" onClick={downloadKit} disabled={!!busy} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold text-sm disabled:opacity-60">
                  {busy === "kit" ? <><FiLoader className="animate-spin" /> Building the kit... {progress}%</> : <><FiPackage /> Download the complete brand kit (ZIP)</>}
                </button>
                <p className="text-xs text-gray-500">The kit holds the logo in colour, for dark backgrounds, in black and in white (SVG and PNG), the symbol alone, favicons, social media pictures, mockups, a one-page brand guide and the colour codes.</p>
              </div>
              {notice && <p className="md:col-span-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 inline-flex items-center gap-2"><FiCheck /> {notice}</p>}
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------------------ saved */}
      {saved.length > 0 && fontsReady && (
        <div className="mt-12">
          <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Saved logos <span className="normal-case tracking-normal text-gray-500 font-normal">- kept in this browser</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {saved.map((s) => (
              <div key={s.id} className="rounded-xl overflow-hidden border border-navy-border bg-white relative">
                <button type="button" onClick={() => { choose(s.config); window.scrollTo({ top: resultRef.current?.offsetTop || 0, behavior: "smooth" }); }} className="block w-full" aria-label={`Open saved logo ${s.config.name}`}>
                  <SvgBox svg={buildLogo(s.config, { uid: `sv${s.id}`, pad: 0.08 }).svg} className="h-28 p-1" />
                </button>
                <button type="button" onClick={() => setSaved((list) => list.filter((x) => x.id !== s.id))} className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/55 text-white hover:bg-red-600" aria-label="Delete saved logo"><FiTrash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center mt-10 max-w-3xl mx-auto">
        Logos are designed in your browser from your words and colours - nothing you type is sent anywhere except the optional tagline ideas. The fonts are free, open-licence Google Fonts,
        so the logo can be used commercially. Check that a name is not already used by another business before you register it as a trademark.
      </p>
    </div>
  );
}

